import React, { useState, useEffect } from 'react';
import { Select, MenuItem, FormControl } from '@mui/material';
import { useDispatch } from 'react-redux';
import { RestApi } from 'common/services/rest-api.service';
import { showSuccess, showError } from 'features/AlertSlice';
import { updateRows } from 'features/GridSlice';
import './VerdictSelect.scss';

interface VerdictSelectProps {
  value: string;
  rowId: string;
  apiEndpoint: string;
  identifierField: string;
  rowData?: any;
}

const VERDICT_OPTIONS = [
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'PROPOSED', label: 'Proposed' },
  { value: 'REJECTED', label: 'Rejected' }
];

export function VerdictSelect(props: VerdictSelectProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [currentValue, setCurrentValue] = useState(props.value || 'PROPOSED');
  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (typeof props?.rowData.isUpdating == "boolean") {
      setIsLoading(props.rowData.isUpdating)
    }
  }, [props.rowData.isUpdating]);

  useEffect(() => {
    const newValue = props.rowData?.verdict || props.value || 'PROPOSED';
    if (newValue !== currentValue) {
      setCurrentValue(newValue);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, props.rowData.verdict]);

  const handleChange = async (event: any) => {
    const newValue = event.target.value;
    if (newValue === currentValue || isLoading) {
      return;
    }

    const previousValue = currentValue;
    setCurrentValue(newValue);
    setIsLoading(true);

    const payload = {
      assets: [props.rowId],
      verdict: newValue,
    };
    RestApi.setData(props.apiEndpoint, payload, 'POST').subscribe(
    () => {
      const updatedRow = props.rowData ? {
        ...props.rowData,
        verdict: newValue,
      } : {
        [props.identifierField]: props.rowId,
        verdict: newValue
      };
      dispatch(updateRows({ rows: [updatedRow], identifier: props.identifierField }));
      dispatch(showSuccess(`Verdict updated to ${VERDICT_OPTIONS.find(opt => opt.value === newValue)?.label}`));
      setIsLoading(false);
    },
    error => {
      setCurrentValue(previousValue);
      dispatch(showError('Failed to update verdict. Please try again.'));
      setIsLoading(false);
    }
  );
  };

  return (
    <FormControl size="small" className="verdict-select" disabled={isLoading}>
      <Select
        value={currentValue}
        onChange={handleChange}
        variant="outlined"
        disabled={isLoading}
        className={`verdict-select-input verdict-select-input--${currentValue.toLowerCase()}`}
        sx={{
          minWidth: 120,
          height: 32,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e0e0e0',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4A79E7',
          },
        }}
      >
        {VERDICT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            className={`verdict-option verdict-option--${option.value.toLowerCase()}`}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
